import type { AppPreferencesRepository } from '../../application/ports/AppPreferencesRepository';
import type { BackupRepository } from '../../application/ports/BackupRepository';
import type { BackupTransactions } from '../../application/ports/BackupTransactions';
import type { DailyRoutineRepository } from '../../application/ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../../application/ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../../application/ports/EmotionalProtocolLogRepository';
import type { ExerciseLogRepository } from '../../application/ports/ExerciseLogRepository';
import type { JournalRepository } from '../../application/ports/JournalRepository';
import type { LocalDatabase } from '../../application/ports/LocalDatabase';
import type { PhilosophyRepository } from '../../application/ports/PhilosophyRepository';
import type { ProfileRepository } from '../../application/ports/ProfileRepository';
import type { RoutinePersistenceTransactions } from '../../application/ports/RoutinePersistenceTransactions';
import type { RoutineTemplateRepository } from '../../application/ports/RoutineTemplateRepository';
import type { RoutineStepTemplateRepository } from '../../application/ports/RoutineStepTemplateRepository';
import type { WorkoutPersistenceTransactions } from '../../application/ports/WorkoutPersistenceTransactions';
import type { WorkoutRepository } from '../../application/ports/WorkoutRepository';
import { AscendInvictusDatabase } from './AscendInvictusDatabase';
import { DexieBackupTransactions } from './DexieBackupTransactions';
import { DexieLocalDatabase } from './DexieLocalDatabase';
import { DexieRoutinePersistenceTransactions } from './DexieRoutinePersistenceTransactions';
import { DexieWorkoutPersistenceTransactions } from './DexieWorkoutPersistenceTransactions';
import { DexieAppPreferencesRepository } from './repositories/DexieAppPreferencesRepository';
import { DexieBackupRepository } from './repositories/DexieBackupRepository';
import { DexieDailyRoutineRepository } from './repositories/DexieDailyRoutineRepository';
import { DexieDailyStepRepository } from './repositories/DexieDailyStepRepository';
import { DexieEmotionalProtocolLogRepository } from './repositories/DexieEmotionalProtocolLogRepository';
import { DexieExerciseLogRepository } from './repositories/DexieExerciseLogRepository';
import { DexieJournalRepository } from './repositories/DexieJournalRepository';
import { DexiePhilosophyRepository } from './repositories/DexiePhilosophyRepository';
import { DexieProfileRepository } from './repositories/DexieProfileRepository';
import { DexieRoutineTemplateRepository } from './repositories/DexieRoutineTemplateRepository';
import { DexieRoutineStepTemplateRepository } from './repositories/DexieRoutineStepTemplateRepository';
import { DexieWorkoutRepository } from './repositories/DexieWorkoutRepository';

export interface PersistenceAdapters {
  readonly database: LocalDatabase;
  readonly profiles: ProfileRepository;
  readonly dailyRoutines: DailyRoutineRepository;
  readonly dailySteps: DailyStepRepository;
  readonly routineTemplates: RoutineTemplateRepository;
  readonly routineStepTemplates: RoutineStepTemplateRepository;
  readonly journals: JournalRepository;
  readonly emotionalProtocolLogs: EmotionalProtocolLogRepository;
  readonly workouts: WorkoutRepository;
  readonly exerciseLogs: ExerciseLogRepository;
  readonly philosophy: PhilosophyRepository;
  readonly appPreferences: AppPreferencesRepository;
  readonly backups: BackupRepository;
  readonly routineTransactions: RoutinePersistenceTransactions;
  readonly workoutTransactions: WorkoutPersistenceTransactions;
  readonly backupTransactions: BackupTransactions;
}

export function createDexiePersistence(databaseName?: string): PersistenceAdapters {
  const database = new AscendInvictusDatabase(databaseName);
  return {
    database: new DexieLocalDatabase(database),
    profiles: new DexieProfileRepository(database),
    dailyRoutines: new DexieDailyRoutineRepository(database),
    dailySteps: new DexieDailyStepRepository(database),
    routineTemplates: new DexieRoutineTemplateRepository(database),
    routineStepTemplates: new DexieRoutineStepTemplateRepository(database),
    journals: new DexieJournalRepository(database),
    emotionalProtocolLogs: new DexieEmotionalProtocolLogRepository(database),
    workouts: new DexieWorkoutRepository(database),
    exerciseLogs: new DexieExerciseLogRepository(database),
    philosophy: new DexiePhilosophyRepository(database),
    appPreferences: new DexieAppPreferencesRepository(database),
    backups: new DexieBackupRepository(database),
    routineTransactions: new DexieRoutinePersistenceTransactions(database),
    workoutTransactions: new DexieWorkoutPersistenceTransactions(database),
    backupTransactions: new DexieBackupTransactions(database),
  };
}
